/**
 * @file helper
 * @author Justineo, Chestnut
 */
import {defaults, includes, get, isFunction} from 'lodash';
import {parseDate} from './date';
import format from 'date-fns/format';
import subDays from 'date-fns/sub_days';
import isValid from 'date-fns/is_valid';

function removeScriptTag(script) {
    if (script && script.parentNode) {
        script.parentNode.removeChild(script);
    }
}

function parseTime(time, days) {
    if (time) {
        return parseDate(time);
    }
    return subDays(new Date(), days);
}

/**
 * 生成时间段数据
 *
 * 未指定`begin`及`end`时默认取最近7天
 *
 * 有两种重载：
 * 1. getTimeRange(options)
 * 2. getTimeRange(begin, end, options)
 *
 * @param {Date|string} [begin] 开始日期
 * @param {Date|string} [end] 结束日期
 * @param {Object} [options] 生成选项
 * @param {string} [options.outputFormat] 输出参数的格式
 * @param {string} [options.beginKey] 结果对象开始时间的键名
 * @param {string} [options.endKey] 结果对象结束时间的键名
 * @return {Object|Array} 时间段的数据，格式由`options`参数决定
 */
export function getTimeRange(begin, end, options) {

    // 只有一个参数时，认为是options
    if (arguments.length === 1) {
        options = begin;
    }

    let defaultOption = {
        outputFormat: 'Date'
    };

    options = defaults({}, options, defaultOption);

    // 解析输入，没有则使用默认时间
    begin = parseTime(begin, 7);
    end = parseTime(end, 1);

    // 处理输出
    if (options.outputFormat.toLowerCase() === 'date') {
        // 直接输出数组用于DatePicker绑定
        return [begin, end];
    }

    begin = format(begin, options.outputFormat);
    end = format(end, options.outputFormat);

    return {
        [options.beginKey || 'begin']: begin,
        [options.endKey || 'end']: end
    };
}

/**
 * 格式化数字
 *
 * @param {number} number 输入的数字
 * @param {number} [decimals=0] 保留小数位数
 * @param {string} [emptyValue=""] 当输入为空或不是数字时的返回内容，会加前缀
 * @param {string} [prefix=""] 返回的字符串的前缀
 * @return {string}
 */
export function formatNumber(
    number,
    {
        decimals = 0,
        emptyValue = '',
        prefix = ''
    } = {}
) {
    if (number == null || isNaN(number)) {
        return prefix + emptyValue;
    }

    number = parseFloat(number).toFixed(decimals);
    // 分为整数和小数
    const parts = number.split('.');
    let integer = parts[0];
    const decimal = parts[1];
    // 加上千位分隔
    integer = integer.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,');
    // 再拼起来
    let result = prefix + integer;
    if (decimal) {
        result += '.' + decimal;
    }
    return result;
}

/**
 * 格式化时间
 *
 * @param {string} time 一般是后端传来的时间字符串
 * @param {string} outputFormat 要输出显示的时间串格式
 * @param {string} fallbackText 如果 time 格式非法，此时要显示的字符串
 *
 * @return {string}
 */
export function formatTime(
    time,
    {
        outputFormat = 'YYYY-MM-DD HH:mm:ss',
        fallbackText = '-'
    } = {}
) {
    let date = parseDate(time);
    return (time || time === 0) && isValid(date)
        ? format(date, outputFormat || 'YYYY-MM-DD HH:mm:ss')
        : fallbackText || '-';
}

/**
 * 判断组件实例是否是某个类型
 *
 * @param {Vue} vm 组件实例
 * @param {string} type 类型
 * @return {boolean} 组件实例是否是指定的类型
 */
export function isType(vm, type) {
    return includes(get(vm, '$options.uiTypes', []), type);
}

/**
 * 动态加载 script 文件
 *
 * @param {string} url 地址
 * @param {Function} cb success 回调
 * @param {Function} errCb error 回调
 * @return {Promise} promise
 */
export function loadScript(url, cb, errCb) {
    if (!url) {
        throw new Error('Require a url to load script.');
    }

    return new Promise((resolve, reject) => {
        let script = document.createElement('script');
        script.setAttribute('src', url);

        script.onload = () => {
            isFunction(cb) && cb();
            removeScriptTag(script);
            script = null;
            resolve();
        };
        script.onerror = e => {
            isFunction(errCb) && errCb();
            removeScriptTag(script);
            script = null;
            reject(e);
        };

        document.head.appendChild(script);
    });
}
